import { Map as ImmutableMap } from 'immutable';
import Version from 'src/new/versions/Version';
import VersionStore from 'src/new/versions/VersionStore';

export enum SelectAction {
    SELECT,
    SELECT_ONLY,
    DESELECT
}

export enum SelectedState {
    NONE,
    SOME,
    ALL
}

export default class SelectedVersions {

    public static create(versions: Version[], allSelected: boolean): SelectedVersions {
        const selections = [];
        for (const version of versions) {
            selections.push([version.getIndex(), allSelected]);
        }
        return new SelectedVersions(ImmutableMap(selections));
    }

    private triggers = new Map<number, number[]>();

    private constructor(private readonly map: ImmutableMap<number, boolean>) {
    }

    public withTrigger(onSelectVersionIndex: number, alsoSelectVersionIndexes: number[]): SelectedVersions {
        this.triggers.set(onSelectVersionIndex, alsoSelectVersionIndexes);
        return this;
    }

    public set(versionIndex: number, action: SelectAction, withTriggers: boolean): SelectedVersions {
        return this.setMany([versionIndex], action, withTriggers);
    }

    public setVersion(version: Version, action: SelectAction, withTriggers: boolean): SelectedVersions {
        return this.setMany([version.getIndex()], action, withTriggers);
    }

    public setVersions(versions: Version[], action: SelectAction, withTriggers: boolean): SelectedVersions {
        return this.setMany(versions.map(version => version.getIndex()), action, withTriggers);
    }

    public setMany(versionIndexes: number[], action: SelectAction, withTriggers: boolean): SelectedVersions {
        return this.createNew(this.map.withMutations((map) => {
            if (action === SelectAction.SELECT_ONLY) {
                map.forEach((value, key) => {
                    if (key !== undefined) {
                        map.set(key, false);
                    }
                });
            }

            const isSelected = action === SelectAction.SELECT || action === SelectAction.SELECT_ONLY;
            versionIndexes.filter(versionIndex => map.has(versionIndex)).forEach(versionIndex => {
                map.set(versionIndex, isSelected);
                if (withTriggers && isSelected) {
                    const alsoSelectVersionIndexes = this.triggers.get(versionIndex);
                    if (alsoSelectVersionIndexes) {
                        for (const alsoSelectVersionIndex of alsoSelectVersionIndexes) {
                            map.set(alsoSelectVersionIndex, true);
                        }
                    }
                }
            });
        }));
    }

    public setAll(isSelected: boolean): SelectedVersions {
        return this.createNew(this.map.withMutations((map) => {
            map.forEach((value, key) => {
                if (key !== undefined) {
                    map.set(key, isSelected);
                }
            });
        }));
    }

    public isSelected(versionIndex: number): boolean {
        return this.map.get(versionIndex);
    }

    public isVersionSelected(version: Version): boolean {
        return this.isSelected(version.getIndex());
    }

    public getSelectedState(): SelectedState {
        return this.getGroupSelectedState(this.map.keySeq().toArray());
    }

    public getGroupSelectedState(versionIndexes: number[]): SelectedState {
        let selectedCount = 0;
        for (const versionIndex of versionIndexes) {
            const isSelected = this.map.get(versionIndex);
            if (isSelected) {
                selectedCount++;
            } else if (selectedCount > 0) {
                return SelectedState.SOME;
            }
        }

        if (selectedCount === 0) {
            return SelectedState.NONE;
        } else if (selectedCount === versionIndexes.length) {
            return SelectedState.ALL;
        } else {
            return SelectedState.SOME;
        }
    }

    public getVersionGroupSelectedState(versions: Version[]): SelectedState {
        return this.getGroupSelectedState(versions.map(version => version.getIndex()));
    }

    public selectedCount(): number {
        return this.map.count(value => value === true);
    }

    public getSelected(): number[] {
        return Array.from(this.map.filter(value => value === true).keySeq().toArray());
    }

    public getSelectedVersions(versions: VersionStore): Version[] {
        return this.getSelected().map(versionIndex => versions.getVersion(versionIndex));
    }

    private createNew(map: ImmutableMap<number, boolean>): SelectedVersions {
        const selectedVersions = new SelectedVersions(map);
        selectedVersions.triggers = this.triggers;
        return selectedVersions;
    }
}
